import { RefObject, useEffect } from 'react';

/**
 * Make the phone browser's own chrome match the page underneath it.
 *
 * A mobile browser paints two strips this app does not own: the status bar
 * above the page and the address bar below it. Safari decides their colour
 * from the DOCUMENT's background, and `index.css` pins `html, body` to white
 * for the whole app — so a navy screen arrives framed by two white bands, with
 * a `theme-color` meta that gets overruled. The same white is what rubber-band
 * overscroll reveals at either end.
 *
 * So the page tells the document what colour it is, rather than the document
 * assuming. The colour is READ from the element rather than passed in: it is
 * always exactly what the browser painted, it cannot drift from the design
 * tokens, and no component has to name a hex the token system owns.
 *
 * Both the document background and `theme-color` are set, because different
 * browsers and versions read different ones, and they must never disagree.
 * Everything is restored on unmount, so leaving the screen puts the app's
 * ordinary light chrome back.
 */
export const useBrowserChromeColor = (ref: RefObject<HTMLElement | null>): void => {
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const color = window.getComputedStyle(element).backgroundColor;
    // A transparent element has nothing to say about the page's colour.
    if (!color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)') return undefined;

    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previous = {
      meta: meta?.getAttribute('content') ?? null,
      html: document.documentElement.style.backgroundColor,
      body: document.body.style.backgroundColor,
    };

    meta?.setAttribute('content', color);
    document.documentElement.style.backgroundColor = color;
    document.body.style.backgroundColor = color;

    return () => {
      if (meta && previous.meta !== null) meta.setAttribute('content', previous.meta);
      document.documentElement.style.backgroundColor = previous.html;
      document.body.style.backgroundColor = previous.body;
    };
  }, [ref]);
};

export default useBrowserChromeColor;
